import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useImmerReducer } from "use-immer";

import { GraphQLAPI, GraphQLResult, queries, parseItem } from "@examspark/api";
import Question, { QuestionContext, reducer } from "@examspark/question-player";
import {
  ExamBlobs,
  ExamItemResponse,
  ExamSessionMetadata,
  ParsedItem,
  Resource,
} from "@examspark/types";

const Styled = styled.div`
  display: flex;
  height: 100%;
  box-sizing: border-box;
`;

interface ContentProps {
  padded?: boolean;
}
const Content = styled.div<ContentProps>`
  height: 100%;
  min-height: 0;
  flex: 1;
  overflow: auto;

  ${(props) => {
    if (props.padded) {
      return `
        margin: 1rem;
      `;
    }
  }}
`;

const Wrapper = styled.div`
  flex: 1;
  height: 100%;
  min-height: 0;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

/**
 * Per @jonscheiding
 *
 *  type Props =
 * ( { data?: ParsedItem } | { itemId?: string } ) &
 * {
 *   condensed?: boolean
 *   ...
 * }
 *
 * However this causes type issues down below. Adding as a possible @todo later.
 */
interface Props {
  data?: ParsedItem;
  response?: ExamItemResponse;
  blobs?: ExamBlobs[string];
  sessionData?: ExamSessionMetadata;
  itemId?: string;
  condensed?: boolean;
  showKey?: boolean;
  onRequestEdit?: (data: ParsedItem) => void;
  toolbar?: React.ReactNode;
  flags?: any;
}

/**
 * Loads and displays preview of an item.
 *
 * Note: Similar to `ItemBrowserPreview` except it is not rendered in
 * `condensed` mode. This component also allows you to pass it item data vs
 * fetching. Might be a good candiate for consolidation sometime.
 */
const StudentItemPreview: React.FC<React.PropsWithChildren<Props>> = (
  props,
) => {
  const [state, dispatch] = useImmerReducer(reducer, {
    response: {},
    blobs: {},
  });
  const [data, setData] = useState<ParsedItem>();
  const [hasLoadError, setHasLoadError] = useState(false);

  const fetchItem = useCallback(async () => {
    if (!props.itemId) return;
    const result = (await GraphQLAPI.query({
      auth: "API_KEY",
      query: queries.item.getItemPreviewStudent,
      variables: {
        id: props.itemId,
        sk: "item#v0",
      },
    })) as GraphQLResult<{
      getResource: Resource;
    }>;

    if (!result.data?.getResource) {
      setHasLoadError(true);
      return;
    }
    setHasLoadError(false);
    setData(parseItem(result.data.getResource));
  }, [props.itemId]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem, props.itemId]);

  useEffect(() => {
    if (typeof state.question !== "undefined") return;
    /** Handle pre-loaded question */
    if (props.data) {
      dispatch({
        type: "INIT",
        data: {
          question: props.data as any,
          viewOnly: true,
        },
      });
      return;
    }

    /** Handle loading question one-by-one */
    if (!data) return;

    dispatch({
      type: "INIT",
      data: {
        question: data as any,
        viewOnly: true,
      },
    });
  }, [data, dispatch, props.data, state.question]);

  const shouldScore = !!(data ?? props.data)?.content?.canvas?.find(
    (canvas) => canvas.scoring,
  );

  useEffect(() => {
    if (!data?.id && !props.data?.id) return;
    if (props.showKey) {
      dispatch({
        type: "REVEAL_KEY",
      });
    } else {
      // only try to score the item if we have scoring data

      dispatch({
        type: "RESPONSE",
        data: {
          score: shouldScore,
          response: props.response,
          blobs: props.blobs,
        },
      });
    }
  }, [
    data?.id,
    dispatch,
    props.response,
    props.blobs,
    props.showKey,
    props.data?.id,
    shouldScore,
  ]);

  // set shuffle if configured
  useEffect(() => {
    if (!props.sessionData?.shuffleSeed) return;
    if (!props.sessionData.shuffleOptions) return;
    if (state.shuffleSeed) return;

    dispatch({
      type: "SHUFFLE",
      seed: props.sessionData.shuffleSeed,
    });
  }, [dispatch, props.sessionData, state]);

  return (
    <QuestionContext.Provider value={[state, dispatch]}>
      <Styled>
        <Main>
          <Content padded={props.condensed}>
            {hasLoadError && <div>Could not load item.</div>}
            {!hasLoadError && (
              <Wrapper>
                <Question
                  condensed={props.condensed}
                  key={props.data?.id ?? data?.id}
                />
              </Wrapper>
            )}
          </Content>
        </Main>
      </Styled>
    </QuestionContext.Provider>
  );
};
export default StudentItemPreview;
